import { useEffect, type ReactNode } from 'react'
import '@fontsource-variable/onest'
import './landing.css'
import { SiteShell } from '@/site/SiteShell'
import type { PageMeta } from '@/site/meta'

const BRAND = 'Anytel'
const SITE = 'anytel.com.ua'
const PHONE = '+380 73 838 47 54'
/** Last edition of each text (shown on the page and as the sitemap lastmod). */
const PRIVACY_UPDATED = { label: '28 жовтня 2026', iso: '2026-10-28' }
const TERMS_UPDATED = { label: '9 жовтня 2026', iso: '2026-10-09' }

/** Which visitor analytics this build has (lib/analytics.ts): the cookie
 * section below describes exactly that, so it can't drift from the site. */
const GA4 = Boolean(import.meta.env.VITE_GA4_ID)
const PLAUSIBLE = Boolean(import.meta.env.VITE_PLAUSIBLE_DOMAIN)

export const privacyMeta: PageMeta = {
  title: 'Політика конфіденційності — Anytel',
  description:
    'Які персональні дані обробляє Anytel, навіщо і як ми їх захищаємо: дані клієнтів, записи дзвінків, строки зберігання.',
  updated: PRIVACY_UPDATED.iso,
}

export const termsMeta: PageMeta = {
  title: 'Умови надання послуг — Anytel',
  description:
    'Умови Anytel: що входить у послугу, пробний період і оплата, обов’язки клієнта, робота AI-агента, відповідальність і дані.',
  updated: TERMS_UPDATED.iso,
}

/** Who signs the contract. Filled in at build time once the legal entity is
 * registered (VITE_LEGAL_ENTITY, e.g. "ФОП Іваненко І. І., РНОКПП 1234567890,
 * м. Київ, вул. …"). Until then the pages say details come with the invoice. */
const ENTITY = import.meta.env.VITE_LEGAL_ENTITY
const CONTACT_EMAIL = import.meta.env.VITE_CONTACT_EMAIL ?? ''

function Contacts() {
  return (
    <ul>
      <li>Виконавець: {ENTITY || 'реквізити зазначаються в договорі та рахунку'}</li>
      <li>Телефон: {PHONE}</li>
      {CONTACT_EMAIL && <li>Email: {CONTACT_EMAIL}</li>}
      <li>Сайт: {SITE}</li>
    </ul>
  )
}

function LegalShell({ title, updated, children }: { title: string; updated: string; children: ReactNode }) {
  useEffect(() => {
    const prev = document.title
    document.title = `${title} — ${BRAND}`
    window.scrollTo(0, 0)
    return () => {
      document.title = prev
    }
  }, [title])

  return (
    <SiteShell>
      <main id="main" className="lp-wrap lp-legal">
        <h1>{title}</h1>
        <p className="lp-muted">Редакція від {updated}</p>
        {children}
      </main>
    </SiteShell>
  )
}

export function PrivacyPage() {
  return (
    <LegalShell title="Політика конфіденційності" updated={PRIVACY_UPDATED.label}>
      <p>
        Ця політика пояснює, які персональні дані обробляє сервіс {BRAND} ({SITE}), навіщо і як ми їх
        захищаємо. Вона складена відповідно до Закону України «Про захист персональних даних».
      </p>

      <h2>1. Дві ролі, у яких ми обробляємо дані</h2>
      <p>
        <strong>Як володілець</strong> — щодо даних наших клієнтів і відвідувачів сайту: ім'я, email,
        телефон контактної особи, дані для рахунків, а також номер, залишений у формі «Замовити
        дзвінок».
      </p>
      <p>
        <strong>Як розпорядник</strong> — щодо даних абонентів, які телефонують або пишуть нашим
        клієнтам: номер телефону, записи й розшифровки розмов, повідомлення в чатах, дані заявок.
        Володільцем цих даних є наш клієнт (компанія, яка підключила {BRAND}). Ми обробляємо їх лише за
        його дорученням і для надання сервісу.
      </p>

      <h2>2. Мета обробки</h2>
      <ul>
        <li>надання сервісу: прийом і маршрутизація дзвінків, робота AI-агента, запис і розшифровка, заявки;</li>
        <li>зв'язок з клієнтами, підтримка, виставлення рахунків;</li>
        <li>безпека: захист від зловживань, спаму і несанкціонованого доступу;</li>
        <li>виконання вимог законодавства.</li>
      </ul>
      <p>Ми не продаємо персональні дані і не використовуємо записи розмов клієнтів для реклами.</p>

      <h2>3. Записи дзвінків і AI</h2>
      <p>
        Якщо клієнт увімкнув запис, розмова зберігається в захищеному сховищі з доступом лише через кабінет. Для роботи
        AI-агента і розшифровки аудіо передається постачальникам мовних моделей (OpenAI, Google,
        ElevenLabs — залежно від налаштувань клієнта) за їхніми умовами обробки даних для бізнесу.
        Клієнт зобов'язаний попереджати абонентів про запис і про те, що відповідає віртуальний
        асистент; сценарії {BRAND} мають для цього готове повідомлення.
      </p>

      <h2>4. Строк зберігання</h2>
      <p>
        Дані зберігаються, доки діє договір з клієнтом, і видаляються або повертаються клієнту після
        його завершення, якщо довший строк не вимагає закон (наприклад, бухгалтерські документи).
        Клієнт може будь-коли попросити видалити окремі записи, дзвінки чи заявки.
      </p>

      <h2>5. Кому ми передаємо дані</h2>
      <ul>
        <li>хостинг-провайдеру, де розміщені сервери сервісу;</li>
        <li>телефонним операторам (SIP), через яких проходять дзвінки;</li>
        <li>постачальникам AI-моделей — лише аудіо й текст розмови, потрібні для відповіді;</li>
        <li>сервісам доставки повідомлень (email, Telegram, месенджери), які налаштував клієнт;</li>
        {(GA4 || PLAUSIBLE) && (
          <li>
            сервісам аналітики сайту ({[PLAUSIBLE && 'Plausible Analytics', GA4 && 'Google Analytics, лише з вашої згоди']
              .filter(Boolean)
              .join('; ')}) — дані про відвідування anytel.com.ua, див. розділ 8;
          </li>
        )}
        <li>органам влади — лише у випадках, прямо передбачених законом.</li>
      </ul>

      <h2>6. Захист</h2>
      <p>
        Шифрування з'єднань (HTTPS/TLS), ізоляція даних кожної компанії, доступ співробітників за
        ролями (перегляд або редагування), шифрування паролів SIP-транків і ключів інтеграцій,
        регулярні резервні копії бази даних.
      </p>

      <h2>7. Ваші права</h2>
      <p>
        Ви маєте право знати, які ваші дані ми обробляємо, вимагати їх виправлення або видалення,
        відкликати згоду і подати скаргу Уповноваженому Верховної Ради України з прав людини. Якщо
        ви — абонент компанії, яка користується {BRAND}, зверніться спершу до цієї компанії: вона
        вирішує, що робити з вашими даними; ми допоможемо їй виконати ваш запит.
      </p>

      <h2>8. Файли cookie й аналітика</h2>
      <p>
        Для входу в кабінет і збереження налаштувань (наприклад, теми) сайт використовує технічне сховище
        браузера. Без нього кабінет не працює, тому згоди на нього ми не питаємо.
      </p>
      {PLAUSIBLE && (
        <p>
          Статистику відвідувань рахує Plausible Analytics. Він не використовує файлів cookie і не зберігає
          персональних даних: рахує відвідування сторінок і дії на сайті (наприклад, натискання на номер
          телефону чи відправку форми), не ідентифікуючи відвідувача.
        </p>
      )}
      {GA4 && (
        <>
          <p>
            Якщо ви погодились у банері на сайті, ми використовуємо Google Analytics 4. Він зберігає файли
            cookie <code>_ga</code> і <code>_ga_*</code> з випадковим ідентифікатором браузера і передає Google
            дані про відвідування: які сторінки ви відкрили, з якого пристрою і приблизно звідки, які дії
            виконали на сайті. Ми дивимось на ці дані лише зведено, щоб покращувати сайт, і не поєднуємо їх з
            даними кабінету.
          </p>
          <p>
            Без вашої згоди Google Analytics не завантажується. Змінити рішення можна будь-коли кнопкою
            «Налаштування cookie» внизу сайту; після відмови ми видаляємо файли cookie Google Analytics з
            вашого браузера.
          </p>
        </>
      )}
      <p>{GA4 || PLAUSIBLE ? 'Рекламних cookie немає.' : 'Аналітичних і рекламних cookie третіх сторін немає.'}</p>

      <h2>9. Контакти</h2>
      <Contacts />
    </LegalShell>
  )
}

export function TermsPage() {
  return (
    <LegalShell title="Умови надання послуг" updated={TERMS_UPDATED.label}>
      <p>
        Ці умови є публічною пропозицією (офертою) щодо надання доступу до сервісу {BRAND}.
        Оплата рахунку або початок користування кабінетом означає згоду з ними. Окремий письмовий
        договір, якщо його підписано, має пріоритет над цими умовами.
      </p>

      <h2>1. Що ми надаємо</h2>
      <p>
        Доступ до хмарного програмного забезпечення {BRAND}: обробка вхідних і вихідних дзвінків,
        голосові сценарії (IVR), AI-агент, записи й розшифровки, заявки, чат-боти, віджет
        зворотного дзвінка, вебхуки і кабінет зі статистикою.
      </p>
      <p>
        {BRAND} не є оператором електронних комунікацій. Телефонні номери і з'єднання з телефонною
        мережею клієнт отримує від свого SIP-оператора за окремим договором з ним; ми підключаємо
        цей номер до сервісу.
      </p>

      <h2>2. Пробний період і оплата</h2>
      <ul>
        <li>Новому клієнту може надаватися пробний період; його строк вказано в кабінеті.</li>
        <li>Далі — щомісячна абонплата за обраним тарифом і оплата хвилин понад включений пакет.</li>
        <li>Рахунок виставляється на початку місяця, оплата протягом 5 банківських днів.</li>
        <li>Обсяг використаних хвилин видно в кабінеті в реальному часі; рахунок формується за цими даними.</li>
        <li>При простроченні оплати понад 10 днів доступ може бути призупинено з попередженням.</li>
      </ul>

      <h2>3. Обов'язки клієнта</h2>
      <ul>
        <li>використовувати сервіс законно: без спаму, шахрайства, автодзвінків без згоди абонентів;</li>
        <li>попереджати абонентів про запис розмови і про віртуального асистента;</li>
        <li>мати правову підставу для обробки даних своїх абонентів (клієнт — їхній володілець);</li>
        <li>берегти паролі доступу і не передавати акаунт стороннім;</li>
        <li>перевіряти інформацію, яку надає AI-агенту (ціни, графік, послуги).</li>
      </ul>

      <h2>4. AI-агент</h2>
      <p>
        AI-агент відповідає на основі інструкцій і бази знань, які задає клієнт. Як і будь-яка
        мовна модель, він може помилятися. Клієнт відповідає за зміст інструкцій і за рішення,
        прийняті на основі заявок; ми — за технічну роботу сервісу.
      </p>

      <h2>5. Доступність і підтримка</h2>
      <p>
        Ми прагнемо доступності сервісу 99,5% на місяць, крім планових робіт, про які попереджаємо
        заздалегідь. Підтримка — у робочі дні з 9:00 до 18:00 за телефоном {PHONE}; критичні
        збої (дзвінки не приймаються) — без вихідних.
      </p>

      <h2>6. Відповідальність</h2>
      <p>
        Наша сукупна відповідальність обмежена сумою, сплаченою клієнтом за останній місяць. Ми не
        відповідаємо за збої SIP-оператора клієнта, сторонніх AI-постачальників і месенджерів, а
        також за упущену вигоду.
      </p>

      <h2>7. Дані</h2>
      <p>
        Обробка персональних даних — згідно з <a href="/privacy">Політикою конфіденційності</a>.
        Щодо даних абонентів клієнта ми є розпорядником і діємо за його дорученням. Після
        припинення договору клієнт може вивантажити свої дані протягом 30 днів, після чого вони
        видаляються.
      </p>

      <h2>8. Строк дії і припинення</h2>
      <p>
        Договір діє безстроково, з помісячною оплатою. Клієнт може відмовитися від послуг будь-коли,
        повідомивши за 14 днів; сплачений період не повертається, якщо інше не погоджено.
      </p>

      <h2>9. Зміни умов</h2>
      <p>
        Про зміну умов або цін ми повідомляємо щонайменше за 30 днів у кабінеті або листом.
      </p>

      <h2>10. Реквізити і контакти</h2>
      <Contacts />
    </LegalShell>
  )
}
